import type {
    ISbStoriesParams,
    ISbStoryData,
    StoryblokRichTextNode,
} from '@storyblok/js';
import { richTextResolver } from '@storyblok/richtext';
import StoryblokClient from 'storyblok-js-client';
import { useRuntimeConfig } from '#imports';
import type { Image } from '#shared/types/miscellaneous';
import type {
    StoryblokAsset,
    StoryblokRichtext,
} from '#shared/types/storyblok';
import normalizeUrl from '~/utils/normalizeUrl';

const imageFromStoryblokAsset = (asset?: StoryblokAsset): Image => {
    if (!asset || !asset.filename) {
        return { src: '/images/image-placeholder.svg', alt: '' };
    }

    return {
        src: asset.filename,
        alt: asset.alt,
    };
};

const isLivePreview = (): boolean => {
    const route = useRoute();
    return Object.keys(route.query).includes('_storyblok');
};

const richTextToHtml = (content?: StoryblokRichtext | string): string => {
    if (!content) return '';

    if (typeof content === 'string') {
        return content;
    }

    const resolver = richTextResolver<string>({
        tiptapExtensions: {
            link: {
                type: 'mark',
                name: 'link',
                config: {
                    renderHTML({
                        HTMLAttributes,
                    }: {
                        HTMLAttributes: Record<string, unknown>;
                    }) {
                        const isEmailLink = HTMLAttributes.linktype === 'email';
                        const href = isEmailLink
                            ? `mailto:${HTMLAttributes.href as string}`
                            : normalizeUrl(HTMLAttributes.href as string);
                        const target = HTMLAttributes.target as
                            | string
                            | undefined;
                        return [
                            'a',
                            { href, ...(target ? { target } : {}) },
                            0,
                        ];
                    },
                },
            },
        },
    });

    const html = resolver.render(content as StoryblokRichTextNode);

    if (html === '<p></p>') {
        return '';
    }

    return html;
};

async function fetchStories(
    customParams: ISbStoriesParams = {},
): Promise<ISbStoryData[]> {
    const config = useRuntimeConfig();

    const client = new StoryblokClient({
        accessToken: config.public.storyblok.accessToken,
        region: 'us',
    });

    let stories: ISbStoryData[] = [];
    let storyPage = 1;
    let totalStories = 100;

    while ((storyPage - 1) * 100 <= totalStories) {
        const defaultParams: ISbStoriesParams = {
            per_page: 100,
            page: storyPage,
            version: 'published',
            cv: Date.now(),
        };

        const mergedParams = { ...defaultParams, ...customParams };

        const storiesResponse = await client.get('cdn/stories', mergedParams);

        totalStories = storiesResponse.total;
        stories = stories.concat(storiesResponse.data.stories);
        storyPage++;

        // Add delay between requests to avoid rate limiting
        if ((storyPage - 1) * 100 <= totalStories) {
            await new Promise((resolve) => setTimeout(resolve, 300));
        }
    }

    return stories;
}

export { fetchStories, imageFromStoryblokAsset, isLivePreview, richTextToHtml };
